// src/drag.ts
function startIconDrag(iconEl, pointerEvent, initialPixelPos, onMove, onEnd) {
  const startX = pointerEvent.clientX;
  const startY = pointerEvent.clientY;
  const target = pointerEvent.target;
  const pointerId = pointerEvent.pointerId;
  let dragging = false;
  const THRESHOLD = 3;
  function onPointerMove(e) {
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!dragging) {
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) {
        return;
      }
      dragging = true;
      iconEl.classList.add("ex0-icon--dragging");
    }
    onMove(initialPixelPos.x + dx, initialPixelPos.y + dy);
  }
  function onPointerUp(e) {
    try {
      target.releasePointerCapture(pointerId);
    } catch {
    }
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    if (dragging) {
      iconEl.classList.remove("ex0-icon--dragging");
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      onEnd(initialPixelPos.x + dx, initialPixelPos.y + dy);
    }
  }
  try {
    target.setPointerCapture(pointerId);
  } catch {
  }
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  return {
    cleanup() {
      try {
        target.releasePointerCapture(pointerId);
      } catch {
      }
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      iconEl.classList.remove("ex0-icon--dragging");
    }
  };
}

// src/layout.ts
function autoLayout(iconCount, containerWidth, containerHeight, config) {
  const maxRows = Math.max(
    1,
    Math.floor((containerHeight - config.padding * 2) / config.cellHeight)
  );
  const maxCols = Math.max(
    1,
    Math.floor((containerWidth - config.padding * 2) / config.cellWidth)
  );
  const positions = [];
  for (let i = 0; i < iconCount; i++) {
    const col = Math.floor(i / maxRows);
    const row = i % maxRows;
    if (col >= maxCols) {
      const wrappedIndex = i - maxCols * maxRows;
      positions.push({
        col: wrappedIndex >= 0 ? wrappedIndex % maxCols : col,
        row: wrappedIndex >= 0 ? maxRows + Math.floor(wrappedIndex / maxCols) : row
      });
    } else {
      positions.push({ col, row });
    }
  }
  return positions;
}
function snapToGrid(pixelX, pixelY, config) {
  const col = Math.max(
    0,
    Math.round((pixelX - config.padding) / config.cellWidth)
  );
  const row = Math.max(
    0,
    Math.round((pixelY - config.padding) / config.cellHeight)
  );
  return { col, row };
}
function gridToPixel(pos, config) {
  return {
    x: config.padding + pos.col * config.cellWidth,
    y: config.padding + pos.row * config.cellHeight
  };
}
function findNextFreePosition(occupied, containerWidth, containerHeight, config) {
  const maxRows = Math.max(
    1,
    Math.floor((containerHeight - config.padding * 2) / config.cellHeight)
  );
  const maxCols = Math.max(
    1,
    Math.floor((containerWidth - config.padding * 2) / config.cellWidth)
  );
  const occupiedSet = new Set(
    Array.from(occupied.values()).map((p) => `${p.col},${p.row}`)
  );
  for (let col = 0; col < maxCols; col++) {
    for (let row = 0; row < maxRows; row++) {
      if (!occupiedSet.has(`${col},${row}`)) {
        return { col, row };
      }
    }
  }
  return { col: maxCols, row: 0 };
}

// src/types.ts
var DEFAULT_CONFIG = {
  cellWidth: 80,
  cellHeight: 90,
  iconSize: 32,
  padding: 12,
  labelMaxWidth: 70
};

// src/IconGrid.ts
function createIconGrid(options) {
  const config = { ...DEFAULT_CONFIG, ...options.config };
  const container = options.container;
  const positions = /* @__PURE__ */ new Map();
  const icons = /* @__PURE__ */ new Map();
  const elements = /* @__PURE__ */ new Map();
  let usingAutoLayout = !options.initialPositions;
  let activeDrag = null;
  let selectedId = null;
  let justDragged = false;
  const host = document.createElement("div");
  host.className = "ex0-icon-grid";
  container.appendChild(host);
  function createIconElement(icon) {
    const el = document.createElement("div");
    el.className = "ex0-icon";
    el.dataset.iconId = icon.id;
    el.style.width = `${config.cellWidth}px`;
    const imageWrapper = document.createElement("div");
    imageWrapper.className = "ex0-icon-image";
    const img = document.createElement("img");
    img.src = icon.iconSrc;
    img.width = config.iconSize;
    img.height = config.iconSize;
    img.draggable = false;
    imageWrapper.appendChild(img);
    const label = document.createElement("div");
    label.className = "ex0-icon-label";
    label.textContent = icon.label;
    el.appendChild(imageWrapper);
    el.appendChild(label);
    el.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) {
        return;
      }
      justDragged = false;
      const pos = positions.get(icon.id);
      if (!pos) {
        return;
      }
      const pixelPos = gridToPixel(pos, config);
      activeDrag = startIconDrag(
        el,
        e,
        pixelPos,
        // onMove: update element position live
        (x, y) => {
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
        },
        // onEnd: snap to grid, handle swap
        (x, y) => {
          justDragged = true;
          activeDrag = null;
          const newPos = snapToGrid(x, y, config);
          let swapId = null;
          for (const [id, p] of positions) {
            if (id !== icon.id && p.col === newPos.col && p.row === newPos.row) {
              swapId = id;
              break;
            }
          }
          if (swapId) {
            const oldPos = positions.get(icon.id);
            positions.set(swapId, oldPos);
            positions.set(icon.id, newPos);
            usingAutoLayout = false;
            applyPosition(swapId);
          } else {
            positions.set(icon.id, newPos);
            usingAutoLayout = false;
          }
          applyPosition(icon.id);
        }
      );
    });
    el.addEventListener("click", (e) => {
      if (justDragged) {
        justDragged = false;
        return;
      }
      e.stopPropagation();
      selectIcon(icon.id);
    });
    el.addEventListener("dblclick", (e) => {
      if (justDragged) {
        return;
      }
      e.stopPropagation();
      icon.onDoubleClick();
    });
    if (icon.onContextMenu) {
      el.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        icon.onContextMenu(e);
      });
    }
    return el;
  }
  function applyPosition(iconId) {
    const el = elements.get(iconId);
    const pos = positions.get(iconId);
    if (!el || !pos) {
      return;
    }
    const pixel = gridToPixel(pos, config);
    el.style.left = `${pixel.x}px`;
    el.style.top = `${pixel.y}px`;
  }
  function applyAllPositions() {
    for (const id of icons.keys()) {
      applyPosition(id);
    }
  }
  function selectIcon(id) {
    if (selectedId) {
      const prev = elements.get(selectedId);
      if (prev) {
        prev.classList.remove("ex0-icon--selected");
      }
    }
    selectedId = id;
    if (id) {
      const el = elements.get(id);
      if (el) {
        el.classList.add("ex0-icon--selected");
      }
    }
  }
  host.addEventListener("click", () => {
    selectIcon(null);
  });
  function performAutoLayout(width, height) {
    const iconList = Array.from(icons.keys());
    const w = width ?? container.getBoundingClientRect().width;
    const h = height ?? container.getBoundingClientRect().height;
    const layoutPositions = autoLayout(iconList.length, w, h, config);
    for (let i = 0; i < iconList.length; i++) {
      positions.set(iconList[i], layoutPositions[i]);
    }
    applyAllPositions();
  }
  const resizeObserver = new ResizeObserver((entries) => {
    if (usingAutoLayout) {
      const entry = entries[0];
      if (entry) {
        const box = entry.contentBoxSize[0];
        performAutoLayout(box.inlineSize, box.blockSize);
      } else {
        performAutoLayout();
      }
    }
  });
  resizeObserver.observe(container);
  for (const icon of options.icons) {
    icons.set(icon.id, icon);
    const el = createIconElement(icon);
    elements.set(icon.id, el);
    host.appendChild(el);
  }
  if (options.initialPositions) {
    for (const [id, pos] of options.initialPositions) {
      positions.set(id, pos);
    }
    const rect = container.getBoundingClientRect();
    for (const id of icons.keys()) {
      if (!positions.has(id)) {
        positions.set(
          id,
          findNextFreePosition(positions, rect.width, rect.height, config)
        );
      }
    }
    applyAllPositions();
  } else {
    performAutoLayout();
  }
  const handle = {
    addIcon(icon, position) {
      icons.set(icon.id, icon);
      const el = createIconElement(icon);
      elements.set(icon.id, el);
      host.appendChild(el);
      if (position) {
        positions.set(icon.id, position);
        usingAutoLayout = false;
      } else {
        const rect = container.getBoundingClientRect();
        positions.set(
          icon.id,
          findNextFreePosition(positions, rect.width, rect.height, config)
        );
      }
      applyPosition(icon.id);
    },
    removeIcon(id) {
      const el = elements.get(id);
      if (el) {
        host.removeChild(el);
        elements.delete(id);
      }
      icons.delete(id);
      positions.delete(id);
      if (selectedId === id) {
        selectedId = null;
      }
    },
    setPositions(newPositions) {
      for (const [id, pos] of newPositions) {
        positions.set(id, pos);
      }
      usingAutoLayout = false;
      applyAllPositions();
    },
    getPositions() {
      return new Map(positions);
    },
    destroy() {
      if (activeDrag) {
        activeDrag.cleanup();
        activeDrag = null;
      }
      resizeObserver.disconnect();
      container.removeChild(host);
      elements.clear();
      icons.clear();
      positions.clear();
    }
  };
  return handle;
}
export {
  DEFAULT_CONFIG,
  createIconGrid
};
